// Network Performance Monitor for Tawk.to and general network monitoring
class NetworkPerformanceMonitor {
  constructor() {
    this.metrics = {
      websocketConnections: [],
      slowConnections: [],
      errors: [],
      performanceMarks: [],
      networkInfo: null
    };
    this.isEnabled = process.env.NODE_ENV === 'development';
    this.originalWebSocket = window.WebSocket;
    this.originalFetch = window.fetch;
    this.originalXHROpen = window.XMLHttpRequest.prototype.open;
    this.slowThresholds = {
      websocket: 5000, // 5 seconds
      fetch: 10000,    // 10 seconds
      xhr: 10000       // 10 seconds
    };
  }

  // Initialize monitoring
  enable() {
    if (this.isEnabled) {
      this.interceptWebSockets();
      this.interceptFetch();
      this.interceptXHR();
      this.captureNetworkInfo();
      this.setupPerformanceObserver();
    }
  }

  // Disable monitoring
  disable() {
    if (this.isEnabled) {
      window.WebSocket = this.originalWebSocket;
      window.fetch = this.originalFetch;
      window.XMLHttpRequest.prototype.open = this.originalXHROpen;
    }
  }

  // Intercept WebSocket connections
  interceptWebSockets() {
    const self = this;
    window.WebSocket = function(url, protocols) {
      const startTime = performance.now();
      const ws = new self.originalWebSocket(url, protocols);
      
      // Track connection timing
      ws.addEventListener('open', () => {
        const duration = performance.now() - startTime;
        self.logWebSocketConnection(url, duration, 'success');
        
        if (duration > self.slowThresholds.websocket) {
          self.logSlowConnection('websocket', url, duration);
        }
      });

      ws.addEventListener('error', (error) => {
        const duration = performance.now() - startTime;
        self.logWebSocketConnection(url, duration, 'error', error);
        self.logError('WEBSOCKET_ERROR', { url, error, duration });
      });

      ws.addEventListener('close', (event) => {
        self.logWebSocketConnection(url, null, 'closed', { code: event.code, reason: event.reason });
      });

      return ws;
    };
  }

  // Intercept fetch requests
  interceptFetch() {
    const self = this;
    window.fetch = function(input, init) {
      const startTime = performance.now();
      const url = typeof input === 'string' ? input : input.url;
      
      return self.originalFetch.apply(this, arguments)
        .then(response => {
          const duration = performance.now() - startTime;
          if (duration > self.slowThresholds.fetch) {
            self.logSlowConnection('fetch', url, duration);
          }
          return response;
        })
        .catch(error => {
          const duration = performance.now() - startTime;
          self.logError('FETCH_ERROR', { url, error, duration });
          throw error;
        });
    };
  }

  // Intercept XMLHttpRequest
  interceptXHR() {
    const self = this;
    window.XMLHttpRequest.prototype.open = function(method, url, async, user, password) {
      const startTime = performance.now();
      const originalSend = this.send;
      
      this.send = function(body) {
        this.addEventListener('load', () => {
          const duration = performance.now() - startTime;
          if (duration > self.slowThresholds.xhr) {
            self.logSlowConnection('xhr', url, duration);
          }
        });

        this.addEventListener('error', (error) => {
          const duration = performance.now() - startTime;
          self.logError('XHR_ERROR', { url, error, duration });
        });

        return originalSend.call(this, body);
      };

      return self.originalXHROpen.call(this, method, url, async, user, password);
    };
  }

  // Setup performance observer for navigation timing
  setupPerformanceObserver() {
    if ('PerformanceObserver' in window) {
      const observer = new PerformanceObserver((list) => {
        list.getEntries().forEach(entry => {
          if (entry.entryType === 'navigation') {
            this.metrics.networkInfo = {
              dns: entry.domainLookupEnd - entry.domainLookupStart,
              tcp: entry.connectEnd - entry.connectStart,
              ttfb: entry.responseStart - entry.requestStart,
              download: entry.responseEnd - entry.responseStart,
              total: entry.loadEventEnd - entry.navigationStart
            };
          }
        });
      });
      
      observer.observe({ entryTypes: ['navigation'] });
    }
  }

  // Capture network information
  captureNetworkInfo() {
    if ('connection' in navigator) {
      this.metrics.networkInfo = {
        ...this.metrics.networkInfo,
        effectiveType: navigator.connection.effectiveType,
        downlink: navigator.connection.downlink,
        rtt: navigator.connection.rtt,
        saveData: navigator.connection.saveData
      };
    }
  }

  // Log WebSocket connection
  logWebSocketConnection(url, duration, status, details = null) {
    this.metrics.websocketConnections.push({
      url,
      duration,
      status,
      details,
      timestamp: Date.now()
    });
  }

  // Log slow connections
  logSlowConnection(type, url, duration) {
    this.metrics.slowConnections.push({
      type,
      url,
      duration,
      timestamp: Date.now()
    });
  }

  // Log errors
  logError(type, data) {
    this.metrics.errors.push({
      type,
      data,
      timestamp: Date.now()
    });
  }

  // Get WebSocket metrics
  getWebSocketMetrics() {
    return this.metrics.websocketConnections;
  }

  // Get slow connections
  getSlowConnections() {
    return this.metrics.slowConnections;
  }

  // Get errors
  getErrors() {
    return this.metrics.errors;
  }

  // Get all metrics
  getMetrics() {
    return {
      ...this.metrics,
      summary: {
        totalWebSocketConnections: this.metrics.websocketConnections.length,
        slowConnections: this.metrics.slowConnections.length,
        errors: this.metrics.errors.length,
        averageWebSocketTime: this.metrics.websocketConnections.length > 0
          ? this.metrics.websocketConnections
              .filter(conn => conn.duration !== null)
              .reduce((sum, conn) => sum + conn.duration, 0) / 
              this.metrics.websocketConnections.filter(conn => conn.duration !== null).length
          : 0
      }
    };
  }

  // Export metrics for analysis
  exportMetrics() {
    return {
      metrics: this.getMetrics(),
      performanceEntries: performance.getEntriesByType('measure'),
      navigationTiming: performance.getEntriesByType('navigation')[0],
      userAgent: navigator.userAgent,
      timestamp: Date.now()
    };
  }

  // Clear metrics
  clearMetrics() {
    this.metrics = {
      websocketConnections: [],
      slowConnections: [],
      errors: [],
      performanceMarks: [],
      networkInfo: null
    };
  }

  // Check if Tawk.to connections are slow
  isTawkToSlow() {
    const tawkConnections = this.metrics.websocketConnections.filter(
      conn => conn.url.includes('tawk.to')
    );
    
    return tawkConnections.some(conn => 
      conn.duration && conn.duration > this.slowThresholds.websocket
    );
  }

  // Get Tawk.to specific metrics
  getTawkToMetrics() {
    const tawkConnections = this.metrics.websocketConnections.filter(
      conn => conn.url.includes('tawk.to')
    );
    
    return {
      connections: tawkConnections,
      averageTime: tawkConnections.length > 0
        ? tawkConnections
            .filter(conn => conn.duration !== null)
            .reduce((sum, conn) => sum + conn.duration, 0) / 
            tawkConnections.filter(conn => conn.duration !== null).length
        : 0,
      errors: tawkConnections.filter(conn => conn.status === 'error').length,
      slowConnections: tawkConnections.filter(
        conn => conn.duration && conn.duration > this.slowThresholds.websocket
      ).length
    };
  }
}

// Create global instance
const networkMonitor = new NetworkPerformanceMonitor();

// Auto-enable in development
if (process.env.NODE_ENV === 'development') {
  networkMonitor.enable();
}

export default networkMonitor;